import React from "react"
import { Speaker, AgendaRow } from "sharedTypes"
import { AGENDA_ROW_TYPES } from "utilities/enums"
import * as Yup from "yup"
import {
  Text, HStack, FormControl as ChakraFormControl, FormLabel, Switch,
} from "@chakra-ui/react"
import ModalForm from "components/dialogs/ReactHookForm/ModalForm"
import TextField from "components/ReactHookForm/TextField"
import AgendaDateTimeField from "components/ReactHookForm/AgendaDateTimeField"
import RichTextField, { RichTextFieldType } from "components/ReactHookForm/RichTextField"
import Checkboxes from "components/ReactHookForm/Checkboxes"
import SpeakerDisplay from "components/elements/SpeakerDisplay"
import * as api from "api/websites"
import { addAgendaRow, removeAgendaRow, updateAgendaRow } from "context/actions"
import { useQueryClient } from "react-query"
import { successToast } from "utilities/toasts"

import { eventStartDateToAgendaUTC } from "utilities/strings"
import { AgendaFormValues } from "pages/EventShow/WebPage/types"

interface Props {
  eventId: string
  speakers?: Speaker[]
  onHide: () => void
  agendaItem?: AgendaRow
  onChange?: (values: AgendaFormValues) => void
  eventStartDate?: string
  eventTimezone?: string
  agendaRows?: AgendaRow[]
}

const AddAgendaEntry = ({
  eventId,
  speakers = [],
  onHide,
  agendaItem,
  onChange,
  eventStartDate,
  eventTimezone,
  agendaRows = [],
}: Props) => {
  const queryClient = useQueryClient()
  const [addMore, setAddMore] = React.useState(false)
  const resetRef = React.useRef<((values?: any) => void) | null>(null)

  const create = (data) => api.postAgendaRow(eventId, {
    ...data,
    type: AGENDA_ROW_TYPES.item,
  })
    .then((response) => {
      addAgendaRow(queryClient, eventId, response.data)
      successToast({ title: "Agenda updated" })
    })

  const update = (data) => api.putAgendaRow(eventId, data.id, data)
    .then((response) => {
      updateAgendaRow(queryClient, eventId, response.data)
      successToast({ title: "Agenda updated" })
    })

  const onSubmit = (values) => {
    if (agendaItem) {
      return update({ ...values, id: agendaItem.id, type: AGENDA_ROW_TYPES.item }).then(() => {
        onHide()
      })
    }

    return create(values).then(() => {
      if (!addMore) {
        onHide()
        return
      }

      const baseRaw = values.endsAt || values.startsAt
      const baseDate = baseRaw ? new Date(baseRaw) : null

      if (!baseDate || Number.isNaN(baseDate.getTime())) {
        resetRef.current?.(initialValues)
        return
      }

      const nextStart = new Date(baseDate)
      const nextEnd = new Date(baseDate.getTime() + 60 * 60 * 1000)

      resetRef.current?.({
        ...values,
        title: "",
        description: "",
        speakerIds: [],
        startsAt: nextStart.toISOString(),
        endsAt: nextEnd.toISOString(),
      })
    })
  }

  const onDelete = () => {
    if (!agendaItem) return Promise.resolve()

    return api.deleteAgendaRow(eventId, agendaItem.id)
      .then(() => {
        removeAgendaRow(queryClient, eventId, agendaItem!.id)
        successToast({ title: "Agenda entry removed" })
        onHide()
      })
  }

  const validationSchema = Yup.object().shape({
    title: Yup.string().required("Title is required."),
    description: Yup.string(),
    speakerIds: Yup.array().of(Yup.number()),
    startsAt: Yup.date()
      .typeError("Start time is required.")
      .required("Start time is required."),
    endsAt: Yup.date()
      .nullable()
      .when("startsAt", {
        is: (startsAt: unknown) => !!startsAt,
        then: (schema: Yup.DateSchema<Date | null | undefined>) => schema.test(
          "is-after-or-equal",
          "End time must be the same as or after the start time.",
          (value, ctx) => {
            if (!value) return true
            const startsAtValue = ctx.parent?.startsAt
            if (!startsAtValue) return true
            const startTime = new Date(startsAtValue).getTime()
            const endTime = new Date(value as unknown as Date).getTime()
            if (Number.isNaN(startTime) || Number.isNaN(endTime)) return true
            return endTime >= startTime
          },
        ),
      }),
  })

  // Keep dates as strings - DatePickerField will handle conversion
  const defaultStartsAt = (() => {
    if (agendaItem) return undefined
    if (agendaRows.length === 0) {
      if (!eventStartDate) return new Date().toISOString()
      const utc = eventTimezone
        ? eventStartDateToAgendaUTC(eventStartDate, eventTimezone)
        : new Date(eventStartDate).toISOString()
      return utc ?? new Date().toISOString()
    }

    const sorted = [...agendaRows].sort((a, b) => {
      const aEnd = a.endsAt || a.startsAt
      const bEnd = b.endsAt || b.startsAt
      return new Date(aEnd).getTime() - new Date(bEnd).getTime()
    })
    const last = sorted[sorted.length - 1]
    return last.endsAt || last.startsAt
  })()

  const defaultEndsAt = (() => {
    if (!defaultStartsAt) return null
    const start = new Date(defaultStartsAt)
    if (Number.isNaN(start.getTime())) return null
    const end = new Date(start.getTime() + 60 * 60 * 1000)
    return end.toISOString()
  })()

  const initialValues = agendaItem ? {
    title: agendaItem.title || "",
    description: agendaItem.description || "",
    speakerIds: agendaItem.speakerIds || [],
    startsAt: agendaItem.startsAt || "",
    endsAt: agendaItem.endsAt || null,
  } : {
    title: "",
    description: "",
    speakerIds: [],
    startsAt: defaultStartsAt ?? "",
    endsAt: defaultEndsAt,
  }

  return (
    <ModalForm
      key={agendaItem?.id || "new"}
      title={`${agendaItem ? "Edit" : "Add"} Agenda Entry`}
      onClose={onHide}
      initialValues={initialValues}
      validationSchema={validationSchema}
      mode="onBlur"
      onSubmit={onSubmit}
      onDanger={agendaItem ? onDelete : null}
      dangerProps={agendaItem ? { type: "agenda entry", name: agendaItem.title } : undefined}
      disableSubmitWhenNotDirty
      onChange={onChange}
      extraActions={!agendaItem ? (
        <HStack spacing={4} align="center">
          <ChakraFormControl display="flex" alignItems="center">
            <FormLabel htmlFor="add-more-switch" mb="0" fontSize="sm">
              Add more
            </FormLabel>
            <Switch
              id="add-more-switch"
              isChecked={addMore}
              onChange={(e) => setAddMore(e.target.checked)}
              colorScheme="button.primary"
            />
          </ChakraFormControl>
        </HStack>
      ) : undefined}
    >
      {({
        control, watch, errors, setValue, reset,
      }) => {
        resetRef.current = reset
        const [startsAt, endsAt] = watch(["startsAt", "endsAt"])
        const startsAtMessage = errors.startsAt?.message as string | undefined
        const endsAtMessage = errors.endsAt?.message as string | undefined
        const titleMessage = errors.title?.message as string | undefined

        const handleStartValueChange = (date: Date | null) => {
          if (!date) return
          const currentEndRaw = watch("endsAt") as string | null
          if (currentEndRaw) {
            const currentEnd = new Date(currentEndRaw)
            if (!Number.isNaN(currentEnd.getTime()) && date <= currentEnd) {
              return
            }
          }

          const nextEnd = new Date(date.getTime() + 60 * 60 * 1000)
          setValue("endsAt", nextEnd.toISOString(), { shouldValidate: true, shouldDirty: true })
        }

        return (
          <>
            {!agendaItem && (
              <Text mb={8} fontSize="md">
                Create an agenda entry that appears in order based on its start time.
              </Text>
            )}
            <AgendaDateTimeField
              showTimeSelect
              utc
              name="startsAt"
              control={control}
              label="Start Time"
              variant="outline"
              intervalStartDate={startsAt}
              intervalEndDate={endsAt}
              error={!!startsAtMessage}
              errorText={startsAtMessage}
              onValueChange={handleStartValueChange}
            />
            <AgendaDateTimeField
              showTimeSelect
              utc
              name="endsAt"
              control={control}
              label="End Time"
              variant="outline"
              minDate={startsAt ? new Date(startsAt) : undefined}
              intervalStartDate={startsAt}
              intervalEndDate={endsAt}
              error={!!endsAtMessage}
              errorText={endsAtMessage}
            />
            <TextField
              name="title"
              control={control}
              label="Title"
              variant="outline"
              error={!!titleMessage}
              errorText={titleMessage}
            />
            <RichTextField
              name="description"
              control={control}
              label="Description"
              type={RichTextFieldType.MINIMAL}
            />
            <Checkboxes
              label="Speakers"
              name="speakerIds"
              control={control}
              items={speakers}
              itemComponent={SpeakerDisplay}
            />
          </>
        )
      }}
    </ModalForm>
  )
}

export default AddAgendaEntry
